import { isSortArgArray } from 'lib-shared-api-utils';
import { AsyncStatus } from 'lib-shared-types-api';
import type { ConversationAndRequester } from 'lib-shared-types-api';
import orderBy from 'lodash/orderBy';
import { useEffect, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useConversationEventsEffect } from 'utils/conversation/useConversationEventsEffect';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { conversationMatchesSearchParams } from '../conversationMatchesSearchParams/conversationMatchesSearchParams';
import { useConversationsParamsToApiArg } from '../useConversationsParamsToApiArg/useConversationsParamsToApiArg';

type UseConversationsResult = {
  /** The async status of the read conversations api endpoint. */
  readonly asyncStatus: AsyncStatus;
  /** The current page conversations. */
  readonly conversations: readonly ConversationAndRequester[];
  /** Whether there is a next page to navigate to. */
  readonly hasNextPage: boolean;
  /** Whether there is a previous page to navigate to. */
  readonly hasPreviousPage: boolean;
  /** The method for navigating to the next page. */
  readonly nextPage: () => void;
  /** The method for navigating to the previous page. */
  readonly previousPage: () => void;
};

/**
 * A hook that handles fetching, updating conversations on the conversations
 * overview page.
 */
export const useConversations = (): UseConversationsResult => {
  // Governs whether we should skip updating the api arg so that we don't
  // trigger re-fetches when we shouldn't - in particular in response to the arg
  // changing after pagination.
  const skipChangeArg = useRef(false);
  const {
    paramsConversationsApiArg,
    paramsPaginatedApiArg,
    paramsSortedApiArg,
  } = useConversationsParamsToApiArg();
  const { params, setParams } = useAppSearchParams('/conversations');
  const [conversationsAsyncStatus, setConversationsAsyncStatus] =
    useState<AsyncStatus>('uninitialized');
  const [conversations, setConversations] = useState<
    readonly ConversationAndRequester[]
  >([]);
  const {
    arg,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    items,
    changeArg,
    nextPage,
    previousPage,
  } = useLazyPaginatedEndpoint({
    endpoint: conversationsApi.endpoints.readConversations,
    itemsSelector: ({ data: { conversations } }) => conversations,
  });

  // We cannot use the async status from the paginated hook as the current
  // conversations are set in the next event loop cycle. Therefore we need
  // another local async status state.
  useEffect(() => {
    if (asyncStatus !== 'fulfilled') {
      setConversationsAsyncStatus(asyncStatus);
    }
  }, [asyncStatus]);

  useUpdateEffect(() => {
    setConversations(items);
    setConversationsAsyncStatus('fulfilled');
  }, [items]);

  // Handles settings the params if the params are empty.
  useEffect(() => {
    if (Object.keys(params).length === 0) {
      setParams({
        limit: (arg?.limit ?? 50).toString(),
      });
    }
    // We only care when params change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Changes the endpoint arg, which in turn re-fetches the conversations.
  useEffect(() => {
    if (skipChangeArg.current) {
      skipChangeArg.current = false;
    } else {
      changeArg({
        ...paramsConversationsApiArg,
        ...paramsPaginatedApiArg,
        ...paramsSortedApiArg,
      });
    }
  }, [
    changeArg,
    paramsConversationsApiArg,
    paramsPaginatedApiArg,
    paramsSortedApiArg,
  ]);

  // Updates the search params whenever the arg limit and offset changes.
  useUpdateEffect(() => {
    if (
      arg?.limit !== paramsPaginatedApiArg.limit ||
      arg?.offset !== paramsPaginatedApiArg.offset
    ) {
      setParams({
        limit: arg?.limit?.toString(),
        offset: arg?.offset?.toString(),
      });

      // We don't want to re-fetch through changeArg to avoid recursive-ness.
      skipChangeArg.current = true;
    }
    // Only run this when certain arg change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arg?.limit, arg?.offset]);

  // Handles the conversation coming in through WebSocket by determining whether
  // that conversation should be added or removed from the current list of
  // conversations, or ignored entirely as it does not fit the current search
  // params.
  useConversationEventsEffect(({ params: webSocketParams }) => {
    const isFirstPage = !hasPreviousPage;
    const incomingConversation: ConversationAndRequester = {
      conversation: webSocketParams.conversation,
      requester: webSocketParams.requester,
      agent_preference: webSocketParams.agent_preference,
    };

    const isIncomingConversationMatchesParams = conversationMatchesSearchParams(
      incomingConversation.conversation,
      params,
    );
    const isIncomingConversationOnCurrentPage = conversations.some(
      (conversation) =>
        conversation.conversation.id === incomingConversation.conversation.id,
    );

    const sort = isSortArgArray(arg?.sort) ? arg?.sort?.[0] : arg?.sort;
    const hasSort = !!sort;
    const isSortDirectionDescending = sort?.direction
      ? sort.direction === 'desc'
      : true;
    const orderByKey = hasSort ? [`conversation.${sort.key}`] : [];
    const orderByDirection = sort?.direction ? [sort.direction] : [];

    if (isIncomingConversationOnCurrentPage) {
      if (isIncomingConversationMatchesParams) {
        setConversations((previousCurrentPageConversation) =>
          orderBy(
            previousCurrentPageConversation.map((currentPageConversation) =>
              currentPageConversation.conversation.id ==
              incomingConversation.conversation.id
                ? incomingConversation
                : currentPageConversation,
            ),
            orderByKey,
            orderByDirection,
          ),
        );
      } else {
        setConversations((previousConversations) =>
          previousConversations.filter(
            (previousConversation) =>
              previousConversation.conversation.id !==
              incomingConversation.conversation.id,
          ),
        );
      }
    } else {
      if (
        isFirstPage &&
        isIncomingConversationMatchesParams &&
        (isSortDirectionDescending || hasSort)
      ) {
        setConversations((previousConversations) => [
          incomingConversation,
          ...previousConversations,
        ]);
      }
    }
  });

  return {
    asyncStatus: conversationsAsyncStatus,
    conversations,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
  };
};
